import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { PieChart, Pie, Tooltip, Cell, ResponsiveContainer } from "recharts";
import jsPDF from "jspdf";

const STATUS_COLORS = {
  New: "#2053c3",
  Completed: "#0f9f6e",
  Pending: "#dc4d5b",
  "Completed After Deadline": "#f0a33b",
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const Reports = () => {
  const dashboardRef = useRef(null);

  const [filters, setFilters] = useState({
    fromMonth: "",
    fromYear: new Date().getFullYear(),
    toMonth: "",
    toYear: new Date().getFullYear(),
    location: "",
    type: "",
    frequency: "",
    policy: "",
    status: "",
    end_date: "",
  });

  const [pieData, setPieData] = useState([]);
  const [statusTrigger, setStatusTrigger] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationsList, setLocationsList] = useState([]);
  const [typesList, setTypesList] = useState([]);
  const [policiesList, setPoliciesList] = useState([]);
  const statusList = Object.keys(STATUS_COLORS);
  const [yearsList, setYearsList] = useState([]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 5; i += 1) {
      years.push(currentYear + i);
    }
    setYearsList(years);

    const loadDropdownData = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const res = await axios.get("/api/actionables/report-summary/", {
          params: { page_size: 5000 },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        const allRows = res.data.rows || [];

        setLocationsList(
          [...new Set(allRows.map((row) => (row.location || "").trim()).filter(Boolean))].sort()
        );
        setTypesList(
          [...new Set(allRows.map((row) => (row.type || "").trim()).filter(Boolean))].sort()
        );
        setPoliciesList(
          [
            ...new Set(
              allRows
                .map((row) => (row.policy_or_document_name || "").trim())
                .filter(Boolean)
            ),
          ].sort()
        );

        setPieData(res.data.pieData || []);
        setStatusTrigger(res.data.statusTriggerData || []);
        setRows(allRows);
      } catch (err) {
        console.error("Failed to load dropdown data:", err);
      }
    };

    loadDropdownData();
  }, []);

  const fetchReport = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem("accessToken");
      const params = { ...filters, page_size: 1000 };

      const res = await axios.get("/api/actionables/report-summary/", {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      setPieData(res.data.pieData || []);
      setStatusTrigger(res.data.statusTriggerData || []);
      setRows(res.data.rows || []);
    } catch (err) {
      console.error("Failed to load report:", err);
      setPieData([]);
      setStatusTrigger([]);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    if (!dashboardRef.current) return;

    const element = dashboardRef.current;
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
    });

    const imgData = canvas.toDataURL("image/png");
    const imgWidth = 850;
    const pageHeight = 595;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const doc = new jsPDF("l", "pt", "a4");

    let heightLeft = imgHeight;
    let position = 0;

    doc.addImage(imgData, "PNG", 20, 20, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      doc.addPage();
      doc.addImage(imgData, "PNG", 20, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    doc.save("Actionables_Dashboard.pdf");
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="report-page" ref={dashboardRef}>
      <div className="compact-hero">
        <div className="report-header" style={{ marginBottom: 0 }}>
          <div>
            <h1>Actionables Report</h1>
            <p
              style={{
                margin: "10px 0 0",
                color: "rgba(255,255,255,0.82)",
                lineHeight: 1.7,
              }}
            >
              Filter reporting data, review status distribution, and export the
              current dashboard view as a polished PDF.
            </p>
          </div>

          <div className="button-group">
            <button onClick={fetchReport}>Apply Filters</button>
            <button onClick={exportPDF}>Export PDF</button>
          </div>
        </div>
      </div>

      <div className="filters">
        <div className="filter-item">
          <label>End Date</label>
          <input
            type="date"
            name="end_date"
            value={filters.end_date}
            onChange={handleFilterChange}
          />
        </div>

        <div className="filter-item">
          <label>From Month</label>
          <select name="fromMonth" value={filters.fromMonth} onChange={handleFilterChange}>
            <option value="">All</option>
            {MONTH_NAMES.map((month) => (
              <option key={month}>{month}</option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          <label>From Year</label>
          <select name="fromYear" value={filters.fromYear} onChange={handleFilterChange}>
            <option value="">All</option>
            {yearsList.map((year) => (
              <option key={year}>{year}</option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          <label>To Month</label>
          <select name="toMonth" value={filters.toMonth} onChange={handleFilterChange}>
            <option value="">All</option>
            {MONTH_NAMES.map((month) => (
              <option key={month}>{month}</option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          <label>To Year</label>
          <select name="toYear" value={filters.toYear} onChange={handleFilterChange}>
            <option value="">All</option>
            {yearsList.map((year) => (
              <option key={year}>{year}</option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          <label>Location</label>
          <select name="location" value={filters.location} onChange={handleFilterChange}>
            <option value="">All</option>
            {locationsList.map((locationValue) => (
              <option key={locationValue}>{locationValue}</option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          <label>Type</label>
          <select name="type" value={filters.type} onChange={handleFilterChange}>
            <option value="">All</option>
            {typesList.map((typeValue) => (
              <option key={typeValue}>{typeValue}</option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          <label>Policy</label>
          <select name="policy" value={filters.policy} onChange={handleFilterChange}>
            <option value="">All</option>
            {policiesList.map((policy) => (
              <option key={policy}>{policy}</option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          <label>Status</label>
          <select name="status" value={filters.status} onChange={handleFilterChange}>
            <option value="">All</option>
            {statusList.map((statusValue) => (
              <option key={statusValue}>{statusValue}</option>
            ))}
          </select>
        </div>
      </div>

      <div
        className="metric-grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", marginBottom: "1.4rem" }}
      >
        {[
          { label: "Visible Rows", value: rows.length },
          { label: "Statuses", value: statusList.length },
          { label: "Policies", value: policiesList.length },
        ].map((item) => (
          <div key={item.label} className="surface-card" style={{ padding: "1.2rem" }}>
            <div style={{ fontSize: "0.82rem", color: "#65748f", fontWeight: 700 }}>
              {item.label}
            </div>
            <div style={{ marginTop: "8px", fontSize: "1.45rem", fontWeight: 800, color: "#102a71" }}>
              {item.value}
            </div>
          </div>
        ))}
      </div>

      <div className="chart-section">
        <div className="chart-card">
          <h3 style={{ marginTop: 0, color: "#102a71" }}>By Status</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={pieData} dataKey="count" nameKey="status" outerRadius={90} label>
                {pieData.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={STATUS_COLORS[entry.status] || "#94a3b8"}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card table-card">
          <h3 style={{ marginTop: 0, color: "#102a71" }}>Status vs Trigger</h3>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>System</th>
                  <th>External</th>
                </tr>
              </thead>
              <tbody>
                {statusTrigger.map((row) => (
                  <tr key={row.status}>
                    <td>{row.status}</td>
                    <td>{row.system_trigger}</td>
                    <td>{row.external_trigger}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="data-table">
        <h3 style={{ marginTop: 0, color: "#102a71" }}>
          Detailed Actionables ({rows.length})
        </h3>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>No</th>
                  <th>Actionable</th>
                  <th>Name</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Location</th>
                  <th>Type</th>
                  <th>Policy</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <a href={`/actionable/${row.uat_id}`} className="link">
                        {row.actionable_number}
                      </a>
                    </td>
                    <td>{row.actionable_name}</td>
                    <td>{row.end_date}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ background: STATUS_COLORS[row.status] || "#94a3b8" }}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td>{row.location}</td>
                    <td>{row.type}</td>
                    <td>{row.policy_or_document_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .report-page {
          min-height: 100vh;
          padding-top: 24px;
        }

        .button-group button {
          margin-left: 0;
        }

        .filter-item {
          display: flex;
          flex-direction: column;
        }

        .chart-section {
          margin-bottom: 25px;
        }

        .chart-card {
          min-height: 100%;
        }

        .data-table {
          padding: 1.4rem;
        }

        .status-badge {
          min-width: 92px;
          justify-content: center;
        }
      `}</style>
    </div>
  );
};

export default Reports;
